// State + actions for the VocalFitness × Samuel lab.
import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { createEngine } from './engine';
import { imitate, decodeTo44k, loadModel } from './model';
import { alignPhones, alignedToResponse, phonesToResponse } from './phonetic';
import { wavBlob } from './mic';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/vocal-lab`;
let seq = 0;

const enrich = async (clip, dialect, text) => {
  const fd = new FormData();
  fd.append('file', wavBlob(clip.wav), 'clip.wav');
  if (text) fd.append('prompt', text);
  const tr = (await axios.post(`${API}/transcribe`, fd)).data;
  const said = text || tr.text;
  if (!said || !tr.words.length) return { transcript: said || '', aligned: [], vf: null };
  const g = (await axios.post(`${API}/g2p`, { text: said, dialect })).data;
  const aligned = alignPhones(tr.words, g.words, dialect);
  const vf = aligned.length ? alignedToResponse(aligned, { duration: clip.samuel.duration_s, f0: clip.samuel.params.frequency }) : null;
  return { transcript: said, aligned, vf, words: tr.words };
};

export function useSamuelLab({ dialect }) {
  const engineRef = useRef(null);
  if (!engineRef.current) engineRef.current = createEngine();
  const eng = engineRef.current;
  const [clips, setClips] = useState([]);
  const [currentId, setCurrentId] = useState(null);
  const [mode, setMode] = useState('samuel');
  const [status, setStatus] = useState('');
  const [modelReady, setModelReady] = useState(false);
  const [playing, setPlaying] = useState(null);
  const [frac, setFrac] = useState(0);
  const [speed, setSpeedState] = useState(1);
  const [error, setError] = useState('');
  const audioRef = useRef(null);
  const speedRef = useRef(1);
  const current = clips.find((c) => c.id === currentId) || null;

  useEffect(() => { loadModel().then(() => setModelReady(true)).catch(() => setError('Modello Samuel non caricato')); }, []);

  const patch = (id, p) => setClips((cs) => cs.map((c) => (c.id === id ? { ...c, ...p } : c)));
  const responseOf = useCallback((clip, m = mode) => (clip ? (m === 'vf' ? clip.vf || clip.samuel : clip.samuel || clip.vf) : null), [mode]);

  const stopAll = useCallback(() => {
    eng.stop();
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setPlaying(null);
  }, [eng]);

  const playImitation = useCallback(async (clip = current, m = mode, startFrac = 0) => {
    const r = responseOf(clip, m);
    if (!r) return;
    stopAll();
    await eng.resume();
    setPlaying('imitation');
    const key = `cap_${m}`;
    const capture = !clip[key] && startFrac === 0;
    if (capture) eng.startRecording();
    await eng.speak(r, { speed: speedRef.current, startFrac, onProgress: setFrac });
    if (capture) { const cap = await eng.stopRecording(); if (cap) patch(clip.id, { [key]: cap }); }
    setPlaying((p) => (p === 'imitation' ? null : p));
  }, [current, mode, responseOf, eng, stopAll]);

  const playOriginal = useCallback(() => {
    if (!current?.url) return;
    stopAll();
    const a = new Audio(current.url);
    a.playbackRate = speedRef.current; a.preservesPitch = true;
    audioRef.current = a; setPlaying('original');
    a.ontimeupdate = () => setFrac(a.currentTime / (a.duration || 1));
    a.onended = () => { setPlaying(null); setFrac(1); };
    a.play().catch(() => setPlaying(null));
  }, [current, stopAll]);

  const addClip = useCallback(async ({ wav, kind, label, text, url, vf }) => {
    const id = ++seq;
    setError('');
    try {
      let clip = { id, kind, label, text, created: new Date(), url: url || null };
      if (wav) {
        setStatus('Samuel sta ascoltando…');
        const { samuel, analysis } = await imitate(wav);
        clip = { ...clip, wav, samuel, analysis, url: url || URL.createObjectURL(wavBlob(wav)) };
      } else clip = { ...clip, vf };
      setClips((cs) => [...cs.slice(-29), clip]);
      setCurrentId(id); setFrac(0); setStatus('');
      playImitation(clip, wav ? mode : 'vf');
      if (wav) {
        setStatus('Trascrizione IPA in corso…');
        enrich(clip, dialect, text).then((e) => patch(id, e)).catch(() => patch(id, { transcript: '', aligned: [] })).finally(() => setStatus(''));
      }
      return clip;
    } catch (e) {
      setStatus(''); setError(e?.message || 'Elaborazione non riuscita');
      return null;
    }
  }, [dialect, mode, playImitation]);

  const fromBlob = useCallback(async (blobOrUrl, meta) => {
    setStatus('Carico l’audio…');
    try {
      const blob = typeof blobOrUrl === 'string' ? await (await fetch(blobOrUrl)).blob() : blobOrUrl;
      const wav = await decodeTo44k(blob);
      return addClip({ ...meta, wav, url: typeof blobOrUrl === 'string' ? blobOrUrl : null });
    } catch { setStatus(''); setError('Audio non disponibile'); return null; }
  }, [addClip]);

  const fromText = useCallback(async (text) => {
    setStatus('Genero la voce sorgente…');
    try {
      const r = await axios.post(`${API}/tts`, { text, dialect }, { responseType: 'blob' });
      return fromBlob(r.data, { kind: 'tts', label: text.slice(0, 40), text });
    } catch { setStatus(''); setError('Sintesi vocale non disponibile'); return null; }
  }, [dialect, fromBlob]);

  const fromPhones = useCallback((phones, label, opts) => addClip({ kind: 'vf', label, vf: phonesToResponse(phones, opts) }), [addClip]);

  const scrubTo = useCallback((f) => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setPlaying(null); setFrac(f);
    const r = responseOf(current); if (r) eng.scrub(r, f);
  }, [current, responseOf, eng]);

  const setSpeed = useCallback((s) => {
    speedRef.current = s; setSpeedState(s); eng.setSpeed(s);
    if (audioRef.current) audioRef.current.playbackRate = s;
  }, [eng]);

  return { eng, clips, current, currentId, setCurrentId, mode, setMode, status, error, setError, modelReady, playing, frac, speed,
    setSpeed, scrubTo, endScrub: () => eng.endScrub(), playImitation, playOriginal, stopAll, fromBlob, fromText, fromPhones, addClip, responseOf };
}
